import { TimeSlot, toSecond, toSecondFromMidnight, WorkDateType } from "@cervval.js/calendars/src/calendars.definition";
import { ClientActionService } from "@cervval.js/client-action/src/services/client.action.service";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { ALIGN_ITEMS, FLEX_CONTAINER_COLUMN, FLEX_GAP, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { CustomElement } from "@cervval.js/client-html5-spa/navigation";
import { ACTION_SERVICE } from "@cervval.js/common-action/src/services/action.service";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { formatMinuteOrSecondsHHMMAndChooseOverflow, SECONDS_IN_ONE_MINUTE } from "@cervval.js/common/src/tools/time.tools";
import { add, beginOf, toTimestamp, UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools";
import { NOW, TODAY } from "@cervval.js/common/src/tools/timestamps.tools.factory";
import { BOOKING_SERVICE } from "@ehealth_tools/common-booking/src/services/abstractCommonBooking.service";
import { ID } from "@ehealth_tools/common/src/data/propertyTypes/property.types";
import { AbstractClientBookingService } from "../../../services/abstractClientBooking.service";
import { _I18nBookingSliderElement } from "./bookingSliderElement.i18n";
export interface BookingSliderElementOptions {
    /** Duration of the slot in second */
    slotDuration: number;
    /** The user ID for whom the agenda is proposed */
    userId?: ID;
    /** Selected time */
    selected?: UnixTimestamp;
    /** time of the day to draw */
    dayTime?: UnixTimestamp;
    /** Callback on selection of available timeslot */
    onSelect?: (ts: UnixTimestamp) => void;
    /** Heading label */
    label?: string;
}

const defaultBookingSliderElementOptions: BookingSliderElementOptions = {
    slotDuration: 15 * SECONDS_IN_ONE_MINUTE
};


@CustomElement({
    name: "booking-slider-element",
    style: require("./bookingSliderElement.scss"),
    template: require("./bookingSliderElement.html"),
    i18n: require("./bookingSliderElement.i18n").I18nBookingSliderElement(),
    elementCssClasses: [W_100, FLEX_CONTAINER_COLUMN, ALIGN_ITEMS.CENTER, FLEX_GAP.GAP_2]
})
export class BookingSliderElement extends WebComponent<_I18nBookingSliderElement> {

    @Injected(BOOKING_SERVICE)
    private _bookingService!: AbstractClientBookingService;

    @Injected(ACTION_SERVICE)
    private _actionService!: ClientActionService<unknown, unknown>;

    @TypedWebComponentOptions(defaultBookingSliderElementOptions)
    public readonly options!: WebComponentOptions<BookingSliderElementOptions>;

    @TypedHtmlElements({ ensureLoadedClasses: [] })
    public readonly components!: {
        header: HTMLDivElement,
        container: HTMLDivElement
    };

    /** The slots to display -- every slot, available or not are contained here */
    private _slots: (
        /** The start and finish of the slot in second from midnight*/
        TimeSlot & {
            /** Indicates that the slot is selected */
            selected: boolean,
            /** Indicates that the slot is available */
            available: boolean
        })[] = [];

    constructor(options?: Partial<BookingSliderElementOptions>) {
        super();
        this.options.setWithoutRefresh(options);
    }

    public override refresh(reason?: "init" | "pageShow" | "action" | string): void {
        this._computeSlots();
        this._displaySlots();

        this.components.header.innerText = this.options.label ?? "";
    }

    /** Handle the case of a day with no slot (off day) */
    private _displayNoSlot(): void {
        const noSlot = createHtmlElement("div", { innerHTML: this.i18n.noTs, cssClass: ["rotate"] });
        this.components.container.append(noSlot);
    }

    /** Build the slot dom element */
    private _displaySlots(): void {

        this.components.container.innerHTML = "";
        if (this._slots.length === 0) { this._displayNoSlot(); return; }
        for (const slot of this._slots) {
            const classes: string[] = ["ts"];
            if (slot.selected) {
                classes.push("selected");
            }
            let timeSlotText = formatMinuteOrSecondsHHMMAndChooseOverflow(slot.start, true);
            if (slot.available) {
                classes.push("available");
            } else {
                timeSlotText = " - ";
                classes.push("not-available");
            }
            const timeSlot = createHtmlElement("div", { innerHTML: timeSlotText, cssClass: classes });
            this.components.container.append(timeSlot);
            if (slot.available && !slot.selected) {
                timeSlot.addEventListener("click", () => {
                    // On slot selection, cast the callback onSelect if available
                    const today = beginOf(this.options.dayTime ?? TODAY(), "day").uts;
                    this.options.onSelect?.(toTimestamp(today + slot.start));
                });
            }
        }
    }

    /** Compute the slot using the calendar (exception, shedule) and considering the other session for the given user */
    private _computeSlots(): void {
        const today = beginOf(this.options.dayTime ?? TODAY(), "day");
        this._slots = [];
        if (this.options.userId == null) { return; }
        const workCalendarHelper = this._bookingService.getUserWorkCalendarHelper(this.options.userId);
        if (workCalendarHelper == null) {
            getLogConsole().error("The user " + this.options.userId + " should have a calendar");
            return;
        }
        let nextStep = workCalendarHelper.getWorkableDate(today, WorkDateType.START).uts;
        const ruc = this._bookingService.getRUC(this.options.userId);
        let hasBeenSelected = false;
        // Build duration slot when it is possible
        while (nextStep < add(today, { day: 1 }).uts) {
            let selected = false;
            let available = true;
            const nextStepEnd = add(nextStep, { second: this.options.slotDuration });
            // Using RUC, check if the slot fit (nextStep === result)
            const rucResult = this._bookingService.assessResourceOnRUC(ruc, this.options.userId, { duration: { second: this.options.slotDuration }, lag: { second: 0 }, quantity: 1 }, nextStep);
            if (rucResult == null) { break; /** No solution possible, fin des cacahuètes */ }
            if (rucResult !== nextStep) { available = false; } // Loop from this new slot
            const calendaredSlot = workCalendarHelper.getWorkableDate(rucResult, WorkDateType.START); // Check that the slot can start using calendar
            if (calendaredSlot.uts !== nextStep) { available = false; } // Loop from this new date
            const calendaredEnd = workCalendarHelper.getFinish({ start: nextStep, duration: toSecond(this.options.slotDuration) });
            if (nextStep < NOW()) {
                available = false; // Not available if passed
            }
            if (available && calendaredEnd.uts === nextStepEnd.uts) {
                if (!hasBeenSelected && this.options.selected && nextStep >= this.options.selected) {
                    selected = true;
                    hasBeenSelected = true;
                }
                // End using calendar is different from basic addition, suggesting that there is a off slot in between. We cannot predict it so just continue without adding the slot
            } else {
                available = false; // Finish is after a break
            }
            this._slots.push({
                selected,
                start: toSecondFromMidnight(nextStep - today.uts),
                finish: toSecondFromMidnight(calendaredEnd.uts - today.uts),
                available
            });
            nextStep = add(nextStep, { second: this.options.slotDuration }).uts;
        }
    }


}